import { Book, Package, Truck, Wrench, ChartBar, ClockCounterClockwise, Warning } from '@phosphor-icons/react';

export default function Tutorial() {
  const sections = [
    {
      icon: ChartBar,
      title: 'DASBOR',
      desc: 'Ringkasan operasional armada Anda.',
      points: [
        'Empat kartu statistik menampilkan jumlah ban yang ada di stok, terpasang di truk, sedang diperbaiki, dan sudah rusak/scrap.',
        'Grafik Ban Rusak Bulanan menunjukkan tren kerusakan dari waktu ke waktu.',
        'Grafik Jenis Kerusakan membantu mengidentifikasi penyebab kerusakan paling sering.',
        'Log Anomali mencatat setiap ketidakcocokan antara ban yang seharusnya terpasang dan ban yang sebenarnya ditemukan di posisi tersebut.',
        'Peringatan Stok Rendah muncul otomatis jika stok ban tersisa kurang dari 5 unit.',
      ],
    },
    {
      icon: Truck,
      title: 'ARMADA',
      desc: 'Mengelola seluruh armada (mobil & truk) Anda.',
      points: [
        'Klik TAMBAH ARMADA untuk menambah armada baru. Isi Nomor Lambung (angka), pilih Tipe, dan ID opsional.',
        'Pilih tipe: Mobil 4 Roda, Truk 6 Roda, Truk 10 Roda, atau Truk 12 Roda — sistem otomatis membuat layout roda yang sesuai untuk setiap tipe.',
        'Gunakan kotak pencarian untuk mencari armada berdasarkan Nomor Lambung atau ID.',
        'Armada dikelompokkan otomatis menurut tipe — semua Truk 12 Roda di satu grup, semua Mobil 4 Roda di grup lain, dst.',
        'Beralih antara tampilan KARTU dan LIST sesuai preferensi.',
        'Klik unit apa pun untuk membuka tampilan detail dengan diagram roda interaktif.',
      ],
    },
    {
      icon: Package,
      title: 'STOK BAN',
      desc: 'Mendaftarkan dan melacak seluruh ban dalam stok.',
      points: [
        'Klik TAMBAH BAN. Isi Ukuran (12.00R24), Tipe (CB972), dan Kedalaman Tapak (32mm). Field tersedia sebagai placeholder — ketik nilai sesuai stok aktual Anda.',
        'Mode SATU: Ketik nomor seri lalu tekan Enter — form tetap terbuka dan siap untuk ban berikutnya. Cocok untuk memasukkan ban satu per satu tanpa muat ulang halaman.',
        'Mode MASSAL: Tempel 10-30+ nomor seri sekaligus (satu per baris) untuk menambah banyak ban dalam satu klik.',
        'Ban dikelompokkan otomatis berdasarkan ukuran — semua 12.00R24 di satu grup, dst. untuk memudahkan pencarian.',
        'Tab status memisahkan: Stok Baru (belum pernah dipasang), Stok Bekas (sudah dipasang lalu dilepas), Terpasang, Diperbaiki, dan Rusak.',
        'Kolom Umur (KM) menampilkan total kilometer kumulatif ban di sepanjang umurnya — bahkan setelah berpindah armada.',
        'Tombol TANDAI SIAP pada ban "Diperbaiki" mengubah statusnya kembali ke stok untuk dapat dipasang lagi.',
        'Tombol HAPUS pada baris ban memungkinkan menghapus ban yang salah input — hanya tersedia untuk ban yang belum pernah dipasang.',
      ],
    },
    {
      icon: Wrench,
      title: 'PASANG & LEPAS BAN',
      desc: 'Mencatat siklus pasang-lepas ban di setiap posisi roda armada.',
      points: [
        'Buka detail armada → klik posisi roda yang KOSONG (garis putus-putus) untuk membuka dialog Pasang Ban.',
        'Cari ban dari stok berdasarkan nomor seri, lalu masukkan odometer saat ini. Klik KONFIRMASI PASANG.',
        'Untuk MELEPAS: klik posisi roda yang TERPASANG (merah). Sistem akan menampilkan ban yang seharusnya ada di posisi tersebut.',
        'Jika ban di lapangan COCOK, klik YA, LANJUTKAN. Jika TIDAK COCOK, klik TIDAK, TIDAK COCOK — sistem akan mencatat anomali dan meminta Anda mengisi nomor seri ban yang sebenarnya.',
        'Isi odometer saat dilepas, kedalaman tapak, dan pilih kondisi: Siap Pakai (kembali ke stok), Perbaikan (ke "Diperbaiki"), atau Rusak (membuat catatan scrap).',
        'Kedalaman tapak ban akan otomatis diperbarui di Stok Ban sesuai hasil pengukuran terbaru.',
        'Untuk Rusak, pilih satu atau lebih jenis kerusakan, opsional foto, dan catatan.',
      ],
    },
    {
      icon: Warning,
      title: 'ANOMALI',
      desc: 'Mengetahui ketika ada perbedaan antara data dan kondisi lapangan.',
      points: [
        'Anomali tercatat otomatis saat operator melepas ban dan menyatakan ban di posisi tersebut berbeda dari catatan sistem.',
        'Semua anomali muncul di Log Anomali pada Dasbor — menampilkan armada, posisi, ban yang diharapkan, dan ban yang ditemukan.',
      ],
    },
    {
      icon: ClockCounterClockwise,
      title: 'RIWAYAT',
      desc: 'Melacak siklus hidup penuh satu ban.',
      points: [
        'Masukkan nomor seri ban yang ingin dilacak, klik CARI.',
        'Lini masa menampilkan: tanggal masuk → setiap kali ban dipasang (di armada mana, posisi berapa, KM saat pasang) → setiap kali dilepas (KM saat lepas, kondisi, KM berjalan untuk siklus tersebut) → tanggal di-scrap (jika sudah dirusak, beserta jenis kerusakan).',
        'Total KM Berjalan menjumlahkan seluruh siklus hidup ban.',
      ],
    },
  ];

  return (
    <div className="p-4 sm:p-8 max-w-5xl" data-testid="tutorial-page">
      <div className="flex items-center gap-3 mb-2">
        <Book size={32} weight="bold" className="text-red-600" />
        <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-widest text-zinc-900" style={{ fontFamily: 'Chivo' }}>
          CARA PAKAI
        </h1>
      </div>
      <p className="text-sm font-mono text-zinc-600 mb-8">
        Panduan singkat menggunakan GTB Tire Management. Ikuti bagian sesuai modul yang sedang Anda gunakan.
      </p>

      <div className="space-y-6">
        {sections.map((s) => (
          <div key={s.title} className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                <s.icon size={20} weight="bold" className="text-white" />
              </div>
              <div>
                <h2 className="text-lg font-black uppercase tracking-wider text-zinc-900" style={{ fontFamily: 'Chivo' }}>
                  {s.title}
                </h2>
                <p className="text-xs font-mono text-zinc-500">{s.desc}</p>
              </div>
            </div>
            <ul className="space-y-2 mt-4">
              {s.points.map((p, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-zinc-700 font-mono leading-relaxed">
                  <span className="text-red-600 font-bold mt-0.5">›</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="mt-8 bg-zinc-50 border border-zinc-200 rounded-xl p-6">
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-2">TIPS CEPAT</p>
        <ul className="space-y-2 text-sm text-zinc-700 font-mono">
          <li className="flex items-start gap-2"><span className="text-red-600">›</span>Tekan <kbd className="bg-white border border-zinc-300 rounded px-1.5 py-0.5 text-xs">Enter</kbd> di form Tambah Ban mode SATU untuk langsung menyimpan dan input ban berikutnya.</li>
          <li className="flex items-start gap-2"><span className="text-red-600">›</span>Wheel berwarna <span className="text-red-600">merah</span> = terpasang, garis putus-putus = kosong.</li>
          <li className="flex items-start gap-2"><span className="text-red-600">›</span>Umur (KM) ban berlanjut dari armada ke armada — sistem secara otomatis menjumlahkan dari semua siklus pasang-lepas.</li>
        </ul>
      </div>
    </div>
  );
}
